import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DrawerRef } from './drawer-ref';

/**
 * Example component to be used inside a drawer
 * This demonstrates how to receive data and use drawerRef
 */
@Component({
  selector: 'app-example-drawer-content',
  template: `
    <div class="example-drawer-content">
      <form [formGroup]="exampleForm" (ngSubmit)="onSubmit()">
        <div class="form-section">
          <h3>Thông tin cơ bản</h3>
          
          <div class="form-group">
            <label for="name">Tên *</label>
            <input 
              id="name"
              type="text" 
              class="form-control" 
              formControlName="name"
              placeholder="Nhập tên...">
            <div class="error-message" *ngIf="exampleForm.get('name')?.touched && exampleForm.get('name')?.invalid">
              Vui lòng nhập tên
            </div>
          </div>

          <div class="form-group">
            <label for="email">Email *</label>
            <input 
              id="email"
              type="email" 
              class="form-control" 
              formControlName="email"
              placeholder="example@email.com">
            <div class="error-message" *ngIf="exampleForm.get('email')?.touched && exampleForm.get('email')?.invalid">
              Vui lòng nhập email hợp lệ
            </div>
          </div>

          <div class="form-group">
            <label for="description">Mô tả</label>
            <textarea 
              id="description"
              class="form-control" 
              formControlName="description"
              rows="4"
              placeholder="Nhập mô tả..."></textarea>
          </div>
        </div>

        <div class="drawer-actions">
          <button type="button" class="btn btn-secondary" (click)="onCancel()">
            <i class="fa fa-times"></i> Hủy
          </button>
          <button type="submit" class="btn btn-primary" [disabled]="!exampleForm.valid">
            <i class="fa fa-check"></i> Lưu
          </button>
        </div>
      </form>

      <div class="received-data" *ngIf="data">
        <h4>Dữ liệu nhận được:</h4>
        <pre>{{ data | json }}</pre>
      </div>
    </div>
  `,
  styles: [`
    .example-drawer-content {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    form {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .form-section {
      flex: 1;
      overflow-y: auto;
    }

    .form-section h3 {
      margin: 0 0 24px 0;
      font-size: 16px;
      font-weight: 600;
      color: #262626;
      padding-bottom: 12px;
      border-bottom: 1px solid #f0f0f0;
    }

    .form-group {
      margin-bottom: 20px;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      color: #595959;
      font-size: 14px;
    }

    .form-control {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      font-size: 14px;
      font-family: inherit;
      transition: all 0.3s ease;
    }

    .form-control:focus {
      border-color: #1890ff;
      outline: none;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }

    .form-control:disabled {
      background: #f5f5f5;
      cursor: not-allowed;
    }

    textarea.form-control {
      resize: vertical;
      min-height: 80px;
    }

    .error-message {
      margin-top: 4px;
      font-size: 12px;
      color: #ff4d4f;
    }

    .drawer-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      padding-top: 24px;
      margin-top: 24px;
      border-top: 1px solid #f0f0f0;
      flex-shrink: 0;
    }

    .btn {
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      border: none;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .btn:active {
      transform: scale(0.98);
    }

    .btn-secondary {
      background: #fff;
      border: 1px solid #d9d9d9;
      color: #595959;
    }

    .btn-secondary:hover {
      color: #262626;
      border-color: #262626;
    }

    .btn-primary {
      background: #1890ff;
      color: #fff;
    }

    .btn-primary:hover:not(:disabled) {
      background: #40a9ff;
    }

    .btn-primary:disabled {
      background: #f5f5f5;
      color: #bfbfbf;
      cursor: not-allowed;
    }

    .received-data {
      margin-top: 24px;
      padding: 16px;
      background: #f5f5f5;
      border-radius: 4px;
    }

    .received-data h4 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      color: #595959;
    }

    .received-data pre {
      margin: 0;
      font-size: 12px;
      color: #262626;
      white-space: pre-wrap;
      word-wrap: break-word;
    }
  `]
})
export class ExampleDrawerContentComponent implements OnInit {
  // These will be injected by DrawerService
  data: any;
  drawerRef!: DrawerRef;

  exampleForm: FormGroup;

  constructor(private fb: FormBuilder) {
    this.exampleForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      description: ['']
    });
  }

  ngOnInit(): void {
    // Pre-fill form if data is provided
    if (this.data) {
      this.exampleForm.patchValue({
        name: this.data.name || '',
        email: this.data.email || '',
        description: this.data.description || ''
      });
    }
  }

  onSubmit(): void {
    if (this.exampleForm.valid) {
      const formValue = this.exampleForm.value;
      console.log('Form submitted:', formValue);

      // Close drawer and return the result
      this.drawerRef.close({
        success: true,
        data: formValue
      });
    }
  }

  onCancel(): void {
    this.drawerRef.close();
  }
}
